import type { TFunction } from "i18next";
import { QLIK_RELOAD_STATUSES } from "@repo/shared";

type Tone = "info" | "success" | "warning" | "danger" | "neutral";

// Qlik's reload origins (the `type` field). The list is the filter's options;
// a value outside it still shows, as "Other".
export const RELOAD_ORIGINS = [
  "automations",
  "chronos",
  "choreographer",
  "hub",
  "external",
  "data-refresh",
] as const;

const STATUS_TONE: Record<string, Tone> = {
  QUEUED: "info",
  RELOADING: "warning",
  CANCELING: "warning",
  SUCCEEDED: "success",
  FAILED: "danger",
  EXCEEDED_LIMIT: "danger",
  CANCELED: "neutral",
};

export const reloadStatusTone = (status: string): Tone => STATUS_TONE[status] ?? "neutral";

export const reloadStatusLabel = (t: TFunction<"reloads">, status: string): string =>
  (QLIK_RELOAD_STATUSES as readonly string[]).includes(status)
    ? t(`status.${status}`)
    : t("status.other");

export const reloadOriginLabel = (t: TFunction<"reloads">, type: string): string =>
  (RELOAD_ORIGINS as readonly string[]).includes(type) ? t(`origin.${type}`) : t("origin.other");

// Failing in every finished reload of the period, failing since the app's last
// success, or failing now and then between successes.
export const SITUATIONS = ["always", "started", "intermittent"] as const;
export type Situation = (typeof SITUATIONS)[number];

export const SITUATION_TONE = {
  always: "danger",
  started: "warning",
  intermittent: "neutral",
} as const;

export function situationOf(app: {
  failed: number;
  total: number;
  currentStreak: number;
}): Situation {
  if (app.failed === app.total) return "always";
  return app.currentStreak > 0 ? "started" : "intermittent";
}

// "code · message" of a failure, either part optional.
export const failureReason = (errorCode: string | null, message: string | null) =>
  [errorCode, message].filter(Boolean).join(" · ");

// What a one-line list cell shows: the message says why, the code only
// classifies it, and Qlik's generic lead-in would eat the little room there is.
// The whole failureReason goes in the tooltip.
export const shortFailureReason = (errorCode: string | null, message: string | null) =>
  message?.replace(/^failed to complete reload:\s*/i, "") || errorCode || "";

// The app's name, or its id when the collection never saw the app in the items
// list (deleted before the first collection).
export const appLabel = (row: { appId: string; appName: string | null }) =>
  row.appName ?? row.appId;
